import {
  Button,
  ButtonGroup,
  Checkbox,
  Choice,
  closeCurrentDialog,
  closeDialog,
  DataStatus,
  FormControl,
  Label,
  openConfirmDialog,
  Separator,
  Table,
  TableRow,
} from 'platform/components';
import {Box, Grid, GridItem, Heading, HStack, Right, Show, Text, VStack} from 'platform/foundation';
import {useCurrencySymbolFormatter, useFormatCurrency, useFormatNumber} from 'platform/locale';

import {useState} from 'react';
import {UseFormReturn, useWatch} from 'react-hook-form';

import {indexBy, isNil, isNotNil} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty, isString} from 'ramda-adjunct';

import {RentalOrderResponseBody} from '@dms/api/rental';
import {useGetRentalPriceGroupQuery, useListRentalPriceGroupQuery} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {EMPTY_PLACEHOLDER, useTenant, useVatRatesOptions} from '@dms/shared';

import {Nullish, RequiredTestIdProps, useNavigate} from 'shared/web';

import {calculateCommonPrice} from '../../../utils/calculateCommonPrice';
import {useGetHandleUpdateChargedKilometers} from '../hooks/useGetHandleUpdateChargedKilometers';
import {ReturnVehicleFormValues} from '../types/ReturnVehicleFormValues';
import {calculateRentPrice} from '../utils/calculateRentPrice';
import {getPriceListColumns} from '../utils/getPriceListColumns';

interface ChangePriceGroupFormProps extends RequiredTestIdProps {
  formApi: UseFormReturn<ReturnVehicleFormValues>;
  control: FormControl<ReturnVehicleFormValues>;
  rentalOrder: RentalOrderResponseBody | Nullish;
}

export function ChangePriceGroupForm(props: ChangePriceGroupFormProps) {
  const formatNumber = useFormatNumber();
  const formatCurrency = useFormatCurrency();
  const formatCurrencySymbol = useCurrencySymbolFormatter();
  const {tenantCurrency} = useTenant();
  const navigate = useNavigate();

  const priceGroup = useWatch({control: props.control, name: 'usedPriceGroup'});

  const [selectedPriceGroup, setSelectedPriceGroup] = useState<{
    priceGroupId: string | null;
    priceGroupItemId: string | null;
  }>({
    priceGroupId: priceGroup?.rentalPriceGroupId ?? null,
    priceGroupItemId: priceGroup?.rentalPriceGroupItemId ?? null,
  });

  const [handleUpdateChargedKilometers] = useGetHandleUpdateChargedKilometers({
    formApi: props.formApi,
    rentalOrder: props.rentalOrder,
  });

  const {data: rentalPriceGroups, isLoading, isError} = useListRentalPriceGroupQuery({});

  const {
    data: priceGroupDetail,
    isLoading: isLoadingPriceGroupDetail,
    isError: isPriceGroupDetailError,
    isFetching: isFetchingPriceGroupDetail,
  } = useGetRentalPriceGroupQuery(
    {rentalPriceGroupId: selectedPriceGroup?.priceGroupId ?? ''},
    {skip: isNil(selectedPriceGroup?.priceGroupId)}
  );

  const [vatRates] = useVatRatesOptions();
  const vatRatesByValue = indexBy((item) => item.value, vatRates);

  const priceGroupOptions =
    rentalPriceGroups?.map((priceGroup) => ({
      value: priceGroup.rentalPriceGroupId,
      label: priceGroup.name,
      fieldLabel: priceGroup.vatType,
    })) ?? [];

  const handleCheckRow = (priceGroupItemId: string) => {
    setSelectedPriceGroup((prevState) => ({
      priceGroupId: prevState.priceGroupId,
      priceGroupItemId,
    }));
  };

  const handleApplyPriceGroup = () =>
    openConfirmDialog({
      text: i18n.t('entity.rental.labels.confirmChangePriceGroup'),
      id: 'confirm-change-price-group',
      onConfirm: () => {
        props.formApi.setValue('usedPriceGroup', {
          rentalPriceGroupId: selectedPriceGroup.priceGroupId ?? undefined,
          rentalPriceGroupItemId: selectedPriceGroup.priceGroupItemId ?? undefined,
        });

        const activePriceGroupItem = priceGroupDetail?.items.find(
          (item) => item.rentalPriceGroupItemId === selectedPriceGroup.priceGroupItemId
        );

        if (isNil(activePriceGroupItem) || isNil(props.rentalOrder)) {
          return;
        }

        const pricePerKilometer = calculateCommonPrice(
          parseFloat(activePriceGroupItem?.price?.ratePerKilometer ?? '0'),
          vatRates
        );

        const actualMileage = props.formApi.getValues('actualMileage');
        const actualReturnDate = props.formApi.getValues('actualReturnDate');
        const {dailyRate, freeDailyKilometers, paidDays, totalFreeKilometers} = calculateRentPrice(
          activePriceGroupItem,
          props.rentalOrder,
          actualReturnDate
        );

        props.formApi.setValue('rentPrice.dailyRate', dailyRate ?? 0);
        props.formApi.setValue('rentPrice.paidDays', paidDays ?? 0);
        props.formApi.setValue('rentPrice.freeDailyKilometers', freeDailyKilometers ?? 0);
        props.formApi.setValue('rentPrice.totalFreeKilometers', totalFreeKilometers ?? 0);
        props.formApi.setValue(
          'rentPrice.rentalPrice',
          calculateCommonPrice((dailyRate ?? 0) * (paidDays ?? 0), vatRates)
        );
        props.formApi.setValue('rentPrice.pricePerKilometer', pricePerKilometer);

        handleUpdateChargedKilometers(actualMileage, totalFreeKilometers);

        resetDeliveryPriceFields(props.formApi);
        resetOtherPriceFields(props.formApi);
        resetDiscountFields(props.formApi);

        closeDialog('confirm-change-price-group');
        closeCurrentDialog();
      },
    });
  return (
    <VStack spacing={4}>
      <DataStatus isLoading={isLoading} isError={isError}>
        <Grid columns={4} align="center">
          <VStack>
            <Choice
              label={i18n.t('entity.rental.labels.name')}
              value={selectedPriceGroup.priceGroupId}
              options={priceGroupOptions}
              formatOptionLabel={(option, meta) => {
                const vatLabel = vatRatesByValue[option.fieldLabel ?? '']?.label;

                if (meta.context === 'value') {
                  return option.label;
                }

                return (
                  <HStack spacing={2} align="baseline">
                    <Text size="small">{option.label}</Text>

                    <Show when={isNotNilOrEmpty(vatLabel)}>
                      <Text color="secondary" size="xSmall">
                        {vatLabel}
                      </Text>
                    </Show>
                  </HStack>
                );
              }}
              onChange={(value) => {
                if (isString(value)) {
                  setSelectedPriceGroup({priceGroupId: value, priceGroupItemId: null});
                }
              }}
            />
          </VStack>

          <VStack>
            <Label>{i18n.t('general.labels.vat')}</Label>
            <Heading size={4} alternative>
              {isNotNil(selectedPriceGroup.priceGroupId)
                ? (vatRatesByValue[priceGroupDetail?.vatType ?? '']?.label ?? EMPTY_PLACEHOLDER)
                : EMPTY_PLACEHOLDER}
            </Heading>
          </VStack>

          <GridItem span={2}>
            <Right>
              <Button
                title={i18n.t('general.labels.settings')}
                onClick={() => navigate(settingsRoutes.priceGroupList)}
                variant="link"
                leftIcon="action/settings"
              />
            </Right>
          </GridItem>
        </Grid>

        <DataStatus
          spacing={10}
          isEmpty={
            isNilOrEmpty(selectedPriceGroup.priceGroupId) || isNilOrEmpty(priceGroupDetail?.items)
          }
          isLoading={isLoadingPriceGroupDetail || isFetchingPriceGroupDetail}
          isError={isPriceGroupDetailError}
          emptyMessage={
            isNilOrEmpty(priceGroupDetail?.items) &&
            isNotNilOrEmpty(selectedPriceGroup.priceGroupId)
              ? i18n.t('entity.rental.labels.priceGroupWithoutItems')
              : i18n.t('entity.rental.labels.notAssignedPriceGroup')
          }
        >
          <Separator spacing={0} />

          <Table
            columns={getPriceListColumns(formatCurrencySymbol(tenantCurrency))}
            variant="bordered"
          >
            {priceGroupDetail?.items.map((item) => (
              <TableRow
                key={item.rentalPriceGroupItemId}
                actionsWidth="5%"
                onRowClick={() => handleCheckRow(item.rentalPriceGroupItemId)}
              >
                <Box padding={2}>
                  <HStack spacing={4} align="center">
                    <Checkbox
                      value={selectedPriceGroup.priceGroupItemId === item.rentalPriceGroupItemId}
                    />
                    <Text alternative>{item.name || EMPTY_PLACEHOLDER}</Text>
                  </HStack>
                </Box>
                <Box padding={2}>
                  <Text>{formatNumber(item.range.from)}</Text>
                </Box>
                <Box padding={2}>
                  <Text>{formatNumber(item.range.to)}</Text>
                </Box>
                <Box padding={2}>
                  <Text>{i18n.t(`entity.rental.labels.${item.range.period}`)}</Text>
                </Box>
                <Box padding={2}>
                  <Text>{formatCurrency(parseFloat(item.price.rate), tenantCurrency)}</Text>
                </Box>
                <Box padding={2}>
                  <Text>
                    {formatCurrency(parseFloat(item.price.ratePerKilometer), tenantCurrency, 2)}
                  </Text>
                </Box>
                <Box padding={2}>
                  <Text>{formatNumber(item.price.includedKilometers)} Km</Text>
                </Box>
              </TableRow>
            ))}
          </Table>
        </DataStatus>
      </DataStatus>

      <ButtonGroup
        align="right"
        buttons={[
          {
            title: i18n.t('general.actions.cancel'),
            variant: 'secondary',
            onClick: closeCurrentDialog,
          },
          {
            title: i18n.t('general.actions.apply'),
            variant: 'primary',
            onClick: handleApplyPriceGroup,
            isDisabled:
              isNil(selectedPriceGroup.priceGroupItemId) || isNil(selectedPriceGroup.priceGroupId),
          },
        ]}
      />
    </VStack>
  );
}

const resetDiscountFields = (formApi: UseFormReturn<ReturnVehicleFormValues>) => {
  formApi.setValue('rentPrice.discount.percent', 0);
  formApi.setValue('rentPrice.discount.discount.vat', 0);
  formApi.setValue('rentPrice.discount.discount.withoutVat', 0);
  formApi.setValue('rentPrice.discount.discount.withVat', 0);
  formApi.setValue('rentPrice.discount.discount.vatType', 'S');
};

const resetDeliveryPriceFields = (formApi: UseFormReturn<ReturnVehicleFormValues>) => {
  formApi.setValue('rentPrice.deliveryPrice.vat', 0);
  formApi.setValue('rentPrice.deliveryPrice.withoutVat', 0);
  formApi.setValue('rentPrice.deliveryPrice.withVat', 0);
  formApi.setValue('rentPrice.deliveryPrice.vatType', 'S');
};

const resetOtherPriceFields = (formApi: UseFormReturn<ReturnVehicleFormValues>) => {
  formApi.setValue('rentPrice.otherPrice.vat', 0);
  formApi.setValue('rentPrice.otherPrice.withoutVat', 0);
  formApi.setValue('rentPrice.otherPrice.withVat', 0);
  formApi.setValue('rentPrice.otherPrice.vatType', 'S');
};
